import {
  WindowWithPbjs,
  WindowWithClientData,
  WindowWithDataLayer,
  WindowWithGa,
  WindowWithGoogleTag,
  WindowWithAdvertising,
  WindowWithAdSense,
} from '@gumtree/shared/src/types/window';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { getCurrentBreakpoint } from '@gumtree/shared/src/util/breakpoints-service';
import { convertBooleanToAB } from '@gumtree/shared/src/util/experiments-service';
import { sanitiseToOnlyAlphanumeric } from '@gumtree/shared/src/util/string-sanitise';

import initLiveRamp from './framework/liveramp/init';
import dfpTrackBrowserType from './framework/dfp/browser-key-value-builder';
import dfpTrackScreenResolution from './framework/dfp/screen-resolution-key-value';
import dfpTrackTallMobile from './framework/dfp/tall-mobile-key-value';
import dfpTrackBelow360px from './framework/dfp/below-360px-key-value';
import dfpBffSrpResultsSize from './framework/dfp/results-size-key-value';
import dfpTrackLocationId from './framework/dfp/location-id-key-value';
import loadDisplayAds from './framework/dfp/init-display-ads';
import startPrebid from './framework/prebid/start-prebid';
import trackAdsPerformance from './framework/track-ads-performance';
import initMicrosoftClarity from './framework/microsoft-clarity/init';
import { extractPageData } from './framework/util/client-data-extractor';
import loadBingAds from './framework/bing/client-side/loader';
import { isBingEnabled } from './framework/bing/initialisation-util';
import { featuresToKeyValue, GrowthBookAdFeatures } from './framework/util/ad-experiment-utils';

const initDisplay = async (
    window: WindowWithGoogleTag &
        WindowWithDataLayer &
        WindowWithClientData &
        WindowWithGa &
        WindowWithAdvertising
) => {
    const { device, pageType, l1Category, l2Category } = extractPageData(window);

    const hashedUserIdFromDataLayer =
        window.dataLayer &&
        window.dataLayer[0] &&
        window.dataLayer[0].d &&
        window.dataLayer[0].d.ck;

    const serverConfigs = {
        ...window.clientData.displayAds,
        ...window.clientData.displayAds?.config,
    };

    if (serverConfigs?.targeting?.ptg) {
        const trackedGamExperiments = {
            ...GrowthBookAdFeatures,
        };
        const growthBookExperiments = featuresToKeyValue(
            trackedGamExperiments,
            convertBooleanToAB
        ).map(([experimentId, value]) => `${experimentId}-${value}`);
        serverConfigs.targeting.ptg = growthBookExperiments;
    }

    const isNearbyAdsFeatureEnabled =
        featuresToKeyValue(GrowthBookAdFeatures).find(
            (feature) => feature[0] === 'nearby-listing-new-ads'
        )?.[1] === 'B';

    const nearbyAdsCount = window.clientData?.resultsPage?.nearbyAds?.ads?.length || 0;
    const isMobile = device === 'mobile';
    const showNearbySlot1 =
        isNearbyAdsFeatureEnabled &&
        ((isMobile && nearbyAdsCount > 6) || (!isMobile && nearbyAdsCount > 10));
    const showNearbySlot2 =
        isNearbyAdsFeatureEnabled &&
        ((isMobile && nearbyAdsCount > 10) || (!isMobile && nearbyAdsCount > 20));

    const showNearbyAds = { showNearbySlot1, showNearbySlot2 };

    await loadDisplayAds(
        window.googletag,
        window,
        serverConfigs,
        window.clientData.displayAds?.config?.resultsSize,
        l1Category,
        l2Category ? l2Category : null,
        pageType,
        device,
        hashedUserIdFromDataLayer,
        showNearbyAds
    );

    // post initialising display
    dfpTrackTallMobile(window.googletag);
    dfpTrackBelow360px(window.googletag);
    dfpBffSrpResultsSize(window.googletag, window, pageType);
    dfpTrackBrowserType(window.googletag);
    dfpTrackScreenResolution(window.googletag);
    await dfpTrackLocationId(window.googletag);

    trackAdsPerformance({ googletag: window.googletag, ga: window.ga, performance });
};

const initPrebid = async (window: WindowWithGoogleTag & WindowWithPbjs & WindowWithClientData) => {
    const microBiddersVariant =
     featuresToKeyValue(GrowthBookAdFeatures).find(
      ([experimentId]) => experimentId === GrowthBookFeature.MICRO_BIDDERS
     )?.[1] ?? 'OFF';
    
    const isMsft = microBiddersVariant === 'B';

    const { device, pageType } = extractPageData(window);

    const l1Category = window.clientData.displayAds?.config?.l1Category;
    const l2Category = window.clientData.displayAds?.config?.l2Category;
    const l3Category = window.clientData.displayAds?.config?.targeting?.l3;

    const screenSize = getCurrentBreakpoint(window)?.tshirt;

    await startPrebid(
        window.googletag,
        pageType,
        device,
        screenSize,
        l1Category,
        l2Category,
        l3Category,
        '',
        window.clientData.displayAds,
        window,
        isMsft
    );
};

const initBing = async (window: WindowWithClientData) => {
    const bingConfig = window.clientData?.bingAds;
    if (!bingConfig) {
        return;
    }
    const {
        device,
        pageType,
        l1Category,
        numberOfResults = 0,
        currentPage,
        googlePpcVisit,
        breadcrumbs,
    } = extractPageData(window);
    const isZeroResults = numberOfResults === 0;

    const breadCrumbLeaf = breadcrumbs?.links?.length
        ? breadcrumbs.links[breadcrumbs.links.length - 1].name
        : '';
    const sanitisedBreadCrumbLeaf = sanitiseToOnlyAlphanumeric(breadCrumbLeaf);

    const keywords =
        pageType === 'ResultsBrowse' && sanitisedBreadCrumbLeaf
            ? sanitisedBreadCrumbLeaf
            : bingConfig.keywords;
    const clientIp = bingConfig.clientIp;

    const adExperimentVariants = featuresToKeyValue(GrowthBookAdFeatures);

    await loadBingAds({
        device,
        keywords,
        clientIp,
        l1Category,
        googlePpcVisit,
        pageType,
        currentPage,
        isZeroResults,
        window,
        adExperimentVariants,
    });
};

const initAds = (
    window: WindowWithGoogleTag &
        WindowWithPbjs &
        WindowWithClientData &
        WindowWithDataLayer &
        WindowWithGa &
        WindowWithAdvertising &
        WindowWithAdSense
) => {
    if (!window.clientData) {
        throw new Error('clientData not found on window object. Error id: 1uw2');
    }

    const { device, pageType, currentPage, l1Category } = extractPageData(window);

    const userEmail = window.clientData.userData?.email;
    initLiveRamp(window, userEmail);

    initMicrosoftClarity(window);

    const showBing = isBingEnabled({
        pageType,
        l1Category,
        device,
        currentPage,
    });

    Promise.all([showBing && initBing(window), initDisplay(window), initPrebid(window)]);
};

export default initAds;
